import { get, post } from '../../lib/api/client';
import { fromP1, type P1Response, type Page, type PageRequest } from '../../lib/api/paging';

/**
 * The content library (BE reqs §4.3). Assets are JSON-asset rows added by a
 * DevOps deploy; the admin reads them, downloads them, and sees who did.
 *
 *   GET  /admin/content?type=&searchTerm=             the library, with download figures
 *   POST /admin/content/{assetId}/download-tracking   log a download (the caller)
 *   GET  /admin/content/{assetId}/downloads           who downloaded it
 */

export type ContentType = 'social_share_template' | 'ambassador_material';
export const CONTENT_TYPES: ContentType[] = ['social_share_template', 'ambassador_material'];

/** Payload item. Social templates: imageUrl, caption, hashtags. Materials: title, description, downloadUrl, contentType. */
export interface ContentItem {
  title?: string;
  description?: string;
  downloadUrl?: string;
  contentType?: string;
  imageUrl?: string;
  caption?: string;
  hashtags?: string[];
  [key: string]: unknown;
}

export interface ContentAsset {
  id: string;
  type: ContentType;
  /** instagram | facebook | sms, or onboarding_guide | faq | flyer. */
  category: string;
  lang: string;
  data: ContentItem[];
  updatedAt: string;
  downloads: number;
  distinctDownloaders: number;
  lastDownloadedAt?: string;
}

export interface ContentParams extends PageRequest {
  type?: ContentType;
  searchTerm?: string;
}

export async function listContent(params: ContentParams, signal?: AbortSignal): Promise<Page<ContentAsset>> {
  return fromP1(await get<P1Response<ContentAsset>>('/admin/content', { ...params }, signal));
}

export function trackDownload(assetId: string): Promise<unknown> {
  return post(`/admin/content/${assetId}/download-tracking`, {});
}

export interface ContentDownload {
  id: string;
  profileId: string;
  displayName: string;
  email?: string;
  downloadedAt: string;
}

export async function listDownloads(
  assetId: string,
  params: PageRequest,
  signal?: AbortSignal,
): Promise<Page<ContentDownload> & { distinctDownloaders: number }> {
  const res = await get<P1Response<ContentDownload> & { distinctDownloaders: number }>(
    `/admin/content/${assetId}/downloads`,
    { ...params },
    signal,
  );
  return { ...fromP1(res), distinctDownloaders: res.distinctDownloaders };
}

/** The asset's display name and the file to hand out. */
export function assetTitle(a: ContentAsset): string {
  const first = a.data[0];
  return first?.title || first?.caption?.slice(0, 60) || a.category.replace(/_/g, ' ');
}

export function assetFileUrl(a: ContentAsset): string | undefined {
  const first = a.data[0];
  return first?.downloadUrl || first?.imageUrl;
}
